// ClientsTerms.jsx
import React, { useState } from "react";
import PropTypes from "prop-types";
import {
    Container,
    Paper,
    Typography,
    Box,
    Divider,
    Button,
    FormControlLabel,
    Checkbox,
    Stack,
} from "@mui/material";

export default function ClientsTerms({
    lastUpdated = new Date().toLocaleDateString(),
    showAccept = false,
    onAccept = null,
}) {
    const [accepted, setAccepted] = useState(false);

    const handlePrint = () => {
        window.print();
    };

    const handleAccept = () => {
        if (onAccept) onAccept();
    };

    return (
        <Container maxWidth="md" sx={{ py: { xs: 2, md: 4 } }}>
            <Paper elevation={3} sx={{ p: { xs: 2, md: 4 } }}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                    sx={{ mb: 2 }}
                >
                    <Box>
                        <Typography variant="h6" component="h1" gutterBottom>
                            TERMS AND CONDITIONS — SALON CLIENTS (CUSTOMERS)
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary">
                            GroomWell Digital Services – Client Terms of Use
                        </Typography>
                    </Box>

                    <Box textAlign="right">
                        <Typography variant="caption" display="block" color="text.secondary">
                            Last Updated:
                        </Typography>
                        <Typography variant="body2">{lastUpdated}</Typography>
                    </Box>
                </Stack>

                <Divider sx={{ mb: 2 }} />

                <Box
                    sx={{
                        maxHeight: { xs: "55vh", md: "60vh" },
                        overflowY: "auto",
                        pr: 1,
                    }}
                >
                    <Section title="1. Acceptance of Terms">
                        By creating an account or using GroomWell, you agree to these Terms. If you do not agree, do not use the
                        platform.
                    </Section>

                    <Section title="2. Platform Nature">
                        <Typography paragraph>GroomWell is an online platform for:</Typography>
                        <ul>
                            <li>Discovering salons</li>
                            <li>Viewing salon details</li>
                            <li>Booking appointments</li>
                            <li>Leaving reviews</li>
                        </ul>
                        <Typography paragraph>
                            GroomWell does NOT provide salon services. All services are provided by independent salon businesses.
                        </Typography>
                    </Section>

                    <Section title="3. User Responsibilities">
                        <Typography paragraph>You agree to:</Typography>
                        <ul>
                            <li>Provide accurate information</li>
                            <li>Use the platform for lawful purposes</li>
                            <li>Not attempt to abuse or disrupt the system</li>
                            <li>Attend confirmed appointments or cancel responsibly</li>
                        </ul>
                    </Section>

                    <Section title="4. Appointment Bookings">
                        <ul>
                            <li>Booking is a request between you and the salon.</li>
                            <li>Salons are responsible for service quality, delivery, and timeliness.</li>
                            <li>GroomWell is not responsible for salon actions, cancellations, or service delivery.</li>
                        </ul>
                    </Section>

                    <Section title="5. Payments">
                        <ul>
                            <li>You pay salons directly.</li>
                            <li>GroomWell does not handle, manage, or control payments.</li>
                            <li>GroomWell is not responsible for refunds, disputes, overcharging, or pricing issues.</li>
                            <li>All payment disputes must be resolved directly with the salon.</li>
                        </ul>
                    </Section>

                    <Section title="6. Reviews and Feedback">
                        <Typography paragraph>
                            You may leave ratings and reviews based on real experiences. You agree not to:
                        </Typography>
                        <ul>
                            <li>Post abusive or defamatory content</li>
                            <li>Post fake or manipulated reviews</li>
                            <li>Violate privacy or upload copyrighted content</li>
                        </ul>
                        <Typography paragraph>
                            Reviews may be removed if they violate policy.
                        </Typography>
                    </Section>

                    <Section title="7. Liability">
                        <Typography paragraph>GroomWell is not liable for:</Typography>
                        <ul>
                            <li>Salon service quality</li>
                            <li>Pricing disputes</li>
                            <li>Losses, injuries, or damages from salon services</li>
                            <li>Inaccuracies in salon information</li>
                            <li>Delays or cancellations</li>
                            <li>Payment issues</li>
                        </ul>
                        <Typography paragraph>
                            Your interaction with salons is at your own risk.
                        </Typography>
                    </Section>

                    <Section title="8. Account Termination">
                        <Typography paragraph>GroomWell may suspend or terminate accounts for:</Typography>
                        <ul>
                            <li>Policy violations</li>
                            <li>Fraudulent activity</li>
                            <li>Abusive behavior</li>
                            <li>Posting harmful or unlawful content</li>
                        </ul>
                    </Section>

                    <Section title="9. Indemnification">
                        You agree to indemnify GroomWell from claims arising from misuse of the platform.
                    </Section>

                    <Section title="10. Modifications to Terms">
                        Terms may be updated at any time. Continued use means acceptance of updated Terms.
                    </Section>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                >
                    {/* <Box>
                        <Button variant="outlined" onClick={handlePrint} sx={{ mr: 1 }}>
                            Print / Save as PDF
                        </Button>
                    </Box>

                    {showAccept ? (
                        <Box display="flex" alignItems="center" gap={1}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={accepted}
                                        onChange={(e) => setAccepted(e.target.checked)}
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        I have read and accept the Terms & Conditions
                                    </Typography>
                                }
                            />
                            <Button
                                variant="contained"
                                disabled={!accepted}
                                onClick={handleAccept}
                            >
                                Accept
                            </Button>
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            By using GroomWell you agree to these Terms.
                        </Typography>
                    )} */}
                </Stack>
            </Paper>
        </Container>
    );
}

function Section({ title, children }) {
    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {title}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                {children}
            </Typography>
        </Box>
    );
}

Section.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node,
};

ClientsTerms.propTypes = {
    lastUpdated: PropTypes.string,
    showAccept: PropTypes.bool,
    onAccept: PropTypes.func,
};
